/**
 * An endless horizontal strip of videos.
 *
 * The cards are cloned until the track is wider than the viewport plus one full
 * set, and the track offset is kept modulo one set — so the row drifts on its
 * own, can be dragged or trackpad-scrolled in either direction, and never
 * reaches an end in either one.
 *
 * Playback is owned here rather than by the markup: every card autoplays muted
 * and loops, but only the cards near the viewport are actually running, so a
 * strip of twenty clips costs the same as the four you can see. There is no
 * pause path on purpose — the clips are meant to read as GIFs.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Cap on cloning, so a mis-measured card can never loop the filler forever. */
const MAX_SETS = 16;

/** Frames between playback passes. Positions move slowly; 10/s is plenty. */
const PLAYBACK_INTERVAL = 6;

/** Fastest flick we will carry, px/s. */
const MAX_VELOCITY = 4000;

/** Drag distance, px, past which the gesture is a scroll and not a tap. */
const TAP_SLOP = 8;

class VideoStripComponent extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.video-strip__track');
    if (!this.track) return;

    // Re-entrant: the theme editor re-attaches sections, and a second pass must
    // not clone the clones.
    for (const clone of this.track.querySelectorAll('[data-clone]')) clone.remove();

    this.slides = Array.from(this.track.children);
    if (this.slides.length === 0) return;

    this.speed = Number(this.dataset.speed) || 0;
    this.direction = this.dataset.direction === 'right' ? 1 : -1;

    this.offset = 0;
    this.rendered = null;
    this.velocity = 0;
    this.period = 0;
    this.cards = [];
    this.dragging = false;
    this.capturedPointer = null;
    this.dragDistance = 0;
    this.onScreen = true;
    this.frame = 0;
    this.raf = 0;

    this.tick = this.tick.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    this.prepare(this.slides);

    // Cards are sized in CSS, not by the video metadata, so the track can be
    // filled straight away — no waiting on a frame that a background tab will
    // not hand out, and no flash of a half-empty row. A width of zero (hidden
    // ancestor, phone still laying out) is picked up by the resize observer.
    this.layout();
    this.listen();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.pauseAll();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** Muted, looping, controls-free — and muted as a property, which is what the
   * autoplay policies actually read. The attribute alone is not enough on a
   * cloned node. */
  prepare(slides) {
    for (const slide of slides) {
      const video = slide.querySelector('video');
      if (!video) continue;

      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.pause();
    }
  }

  /** Clone whole sets until the track can cover the viewport with one set of
   * slack, then record where every card sits. */
  layout() {
    const viewport = this.offsetWidth;
    let sets = 1;

    while (sets < MAX_SETS) {
      this.measure();
      if (this.period > 0 && this.track.scrollWidth >= viewport + this.period) break;
      this.appendSet();
      sets += 1;
    }

    this.measure();
  }

  appendSet() {
    const fragment = document.createDocumentFragment();

    for (const slide of this.slides) {
      const clone = slide.cloneNode(true);
      clone.dataset.clone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      // A duplicate of a card is scenery, not a second way to reach the page.
      for (const link of clone.querySelectorAll('a')) link.setAttribute('tabindex', '-1');
      fragment.append(clone);
    }

    const added = Array.from(fragment.children);
    this.track.append(fragment);
    this.prepare(added);
  }

  /** One period is the distance from a card to the same card in the next set,
   * which folds in the flex gap without having to read it. */
  measure() {
    const children = Array.from(this.track.children);
    const base = children[0]?.offsetLeft ?? 0;
    const next = children[this.slides.length];

    this.period = next ? next.offsetLeft - base : this.track.scrollWidth;
    this.cards = children.map((element) => ({
      left: element.offsetLeft - base,
      width: element.offsetWidth,
      video: element.querySelector('video'),
    }));
  }

  listen() {
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerUp);
    this.addEventListener('wheel', this.onWheel, { passive: false });
    this.addEventListener('click', this.onClick, true);
    this.addEventListener('dragstart', (event) => event.preventDefault());

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // A resize changes the card height, and therefore the card width, the
    // period and how many sets are needed.
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.layout(), 150);
    });
    this.resizeObserver.observe(this);

    // Off-screen, the whole thing costs nothing: no frames, no decoding.
    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.onScreen = entry.isIntersecting;
        if (this.onScreen) {
          this.start();
        } else {
          this.stop();
          this.pauseAll();
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.intersectionObserver.observe(this);
  }

  start() {
    if (this.raf) return;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  stop() {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.stop();
      this.pauseAll();
    } else if (this.onScreen) {
      this.start();
    }
  }

  tick(now) {
    // Clamped so a backgrounded tab does not resume with one enormous step.
    const elapsed = Math.min(0.064, (now - this.last) / 1000);
    this.last = now;

    if (!this.dragging) {
      if (Math.abs(this.velocity) > 2) {
        this.offset += this.velocity * elapsed;
        this.velocity *= Math.pow(0.002, elapsed);
      } else {
        this.velocity = 0;
        if (this.speed > 0 && !REDUCED_MOTION.matches) {
          this.offset += this.direction * this.speed * elapsed;
        }
      }
    }

    this.apply();

    this.frame += 1;
    if (this.frame % PLAYBACK_INTERVAL === 0) this.updatePlayback();

    this.raf = requestAnimationFrame(this.tick);
  }

  /** Wrap into one period, so the track is always showing the same stretch of
   * cards no matter how far it has been pushed. */
  apply() {
    if (this.period > 0) {
      this.offset = (((this.offset % this.period) + this.period) % this.period) - this.period;
    }

    const next = Math.round(this.offset * 100) / 100;
    if (next === this.rendered) return;

    this.rendered = next;
    this.track.style.transform = `translate3d(${next}px, 0, 0)`;
  }

  updatePlayback() {
    const viewport = this.offsetWidth;
    // A quarter of a viewport of lead-in: at drift speed that is several
    // seconds of warning before a card slides in, and after a hard flick the
    // clip is already in the HTTP cache anyway — every clone shares its source
    // with the card it was copied from.
    const margin = viewport * 0.25;

    for (const card of this.cards) {
      const video = card.video;
      if (!video) continue;

      const left = card.left + this.offset;
      const visible = this.onScreen && left < viewport + margin && left + card.width > -margin;

      if (visible) {
        if (video.paused) {
          video.muted = true;
          video.play().catch(() => {
            /* Low power mode, or a codec the browser will not decode. The
               poster stays up; nothing else to do. */
          });
        }
      } else if (!video.paused) {
        video.pause();
      }
    }
  }

  pauseAll() {
    for (const card of this.cards) card.video?.pause();
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.dragging = true;
    this.dragDistance = 0;
    this.pointerX = event.clientX;
    this.pointerTime = event.timeStamp || performance.now();
    this.velocity = 0;
    this.classList.add('is-dragging');
  }

  onPointerMove(event) {
    if (!this.dragging) return;

    const now = event.timeStamp || performance.now();
    const dx = event.clientX - this.pointerX;
    // Floor the interval: a 0ms gap between coalesced events would otherwise
    // read as infinite speed.
    const dt = Math.max(8, now - this.pointerTime) / 1000;

    this.pointerX = event.clientX;
    this.pointerTime = now;
    this.dragDistance += Math.abs(dx);
    this.offset += dx;

    const velocity = dx / dt;
    this.velocity = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, velocity * 0.7 + this.velocity * 0.3));

    // Taken only once the gesture is clearly a drag, so a tap on a linked card
    // still behaves like a tap.
    if (this.dragDistance > TAP_SLOP && this.capturedPointer === null) {
      try {
        this.setPointerCapture(event.pointerId);
        this.capturedPointer = event.pointerId;
      } catch {
        /* The pointer is already gone; the drag still tracks without capture. */
      }
    }

    this.apply();
  }

  onPointerUp(event) {
    if (!this.dragging) return;

    this.dragging = false;
    this.classList.remove('is-dragging');

    if (this.capturedPointer !== null) {
      try {
        this.releasePointerCapture(this.capturedPointer);
      } catch {
        /* Already released with the pointer itself. */
      }
      this.capturedPointer = null;
    }

    // Held still before letting go: that is a stop, not a flick.
    const idle = (event.timeStamp || performance.now()) - this.pointerTime;
    if (idle > 120) this.velocity = 0;
  }

  /** Horizontal wheel and two-finger trackpad swipes scroll the strip; vertical
   * ones belong to the page. */
  onWheel(event) {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    event.preventDefault();
    this.velocity = 0;
    this.offset -= event.deltaX;
    this.apply();
  }

  /** Swallow the click that ends a drag, so dragging across a linked card does
   * not navigate. */
  onClick(event) {
    if (this.dragDistance <= TAP_SLOP) return;

    event.preventDefault();
    event.stopPropagation();
  }
}

if (!customElements.get('video-strip-component')) {
  customElements.define('video-strip-component', VideoStripComponent);
}
